import { useMemo } from 'react';

import { Flex, Grid, GridItem, Image } from '@chakra-ui/react';
import { Avatar, AvatarImage, Card, CardContent } from '@mesh.ai/rabbit';
import { format } from 'date-fns';
import { useHistory } from 'react-router-dom';

import confettiSrc from 'assets/images/confetti.png';

import { FEEDBACK_FORM_TYPE } from 'constants/ProjectConstants';
import { pushFeedbackParams } from 'constants/helper';

import { PublicPraiseText } from 'uiToolkitV1/TextParseComponent/TextParseComponent';
import { Tooltip } from 'uiToolkitV1/Tooltip';
import { TextBodyMedium, TextLabel } from 'uiToolkitV1/Typography';

import { Employee, FeedbackTableValues } from '../types/feedbacktable';

const ReceivedEmployee = ({
  receivedEmployees,
  isSingledReceivedEmployee,
}: {
  receivedEmployees: Employee[];
  isSingledReceivedEmployee: boolean;
}) => {
  const firstThreeReceivedEmployees = receivedEmployees.slice(0, 3);

  const lengthOfRemainingEmployees = receivedEmployees.length - 3;

  const tooltipText = useMemo(
    () =>
      receivedEmployees
        .slice(3)
        .map((employee) => employee.metadata.displayName)
        .join(', '),
    [receivedEmployees]
  );

  if (isSingledReceivedEmployee) {
    return (
      <Flex flexDir="column" alignItems="center">
        <Avatar
          style={{
            height: '96px',
            width: '96px',
          }}
        >
          <AvatarImage src={receivedEmployees[0]?.profile?.profilePhoto} alt="Avatar" />
        </Avatar>
        <TextLabel textAlign="center" mt="8px" color="text.g2">
          {receivedEmployees[0]?.metadata?.displayName}
        </TextLabel>
      </Flex>
    );
  }
  return (
    <Grid templateColumns="repeat(2, 1fr)" gap={2}>
      {firstThreeReceivedEmployees.map((employee) => (
        <GridItem key={employee.uuid}>
          <Flex flexDir="column" alignItems="center">
            <Avatar
              style={{
                height: '48px',
                width: '48px',
              }}
            >
              <AvatarImage src={employee.profile.profilePhoto} alt="Avatar" />
              <TextLabel color="text.g2">{employee?.metadata?.displayName}</TextLabel>
            </Avatar>
            <TextLabel textAlign="center" mt="8px" color="text.g2">
              {employee?.metadata?.displayName}
            </TextLabel>
          </Flex>
        </GridItem>
      ))}

      {lengthOfRemainingEmployees > 0 && (
        <GridItem display="flex" alignItems="center" justifyContent="center">
          <Tooltip label={tooltipText}>
            <Flex
              alignItems="center"
              justifyContent="center"
              style={{
                background: '#E9F8FC',
                borderRadius: '50%',
                height: '48px',
                width: '48px',
              }}
            >
              <TextBodyMedium color="#0D819F" bold>
                +{lengthOfRemainingEmployees}
              </TextBodyMedium>
            </Flex>
          </Tooltip>
        </GridItem>
      )}
    </Grid>
  );
};

const PublicPraiseCard = ({ comment, date, employee }: FeedbackTableValues) => {
  const history = useHistory();
  const formattedDate = format(new Date(date.value), 'MMM dd, yyyy');
  const receivedEmployeeIds = Object.keys(comment.value.macros.employees);
  const receivedEmployees = receivedEmployeeIds.map((id) => comment.value.macros.employees[id]);
  const isSingledReceivedEmployee = receivedEmployees.length === 1;

  const onCardClick = () => {
    // @ts-expect-error not typed
    pushFeedbackParams({
      history,
      formType: FEEDBACK_FORM_TYPE.SHOW_RECEIVED,
      typeMapped: true,
      feedbackId: comment.uuid,
    });
  };

  return (
    <Card
      style={{
        marginBottom: '12px',
        minHeight: '172px',
        background: 'linear-gradient(to bottom, rgba(219, 242, 211, 0.08), rgba(219, 242, 211, 0.04))',
        position: 'relative',
        overflow: 'hidden',
        cursor: 'pointer',
      }}
      onClick={onCardClick}
    >
      <Image
        src={confettiSrc}
        width="90px"
        alt=""
        rotate="180deg"
        position="absolute"
        left="0"
        top="0"
        userSelect="none"
        zIndex={1}
      />
      <Image
        src={confettiSrc}
        width="90px"
        alt=""
        // rotate="180deg"
        position="absolute"
        right="0"
        bottom="-40px"
        userSelect="none"
        zIndex={1}
      />
      <CardContent
        style={{
          position: 'relative',
          zIndex: 2,
        }}
      >
        <Grid templateColumns="1fr 3fr 1fr" gap={4}>
          <GridItem>
            <ReceivedEmployee
              receivedEmployees={receivedEmployees}
              isSingledReceivedEmployee={isSingledReceivedEmployee}
            />
          </GridItem>
          <GridItem>
            <Flex
              // alignItems="center"
              position="relative"
              // className={publicPraiseStyles.publicPraiseQuote}
              w="fit-content"
            >
              {/* @ts-expect-error not typed */}
              <PublicPraiseText text={comment.value.text} macros={comment.value.macros} />
            </Flex>
          </GridItem>
          <GridItem>
            <Flex flexDir="column" alignItems="flex-end" mr="12px" h="100%">
              <TextLabel color="text.g2">{formattedDate}</TextLabel>
              <Flex alignItems="center" mt="auto">
                <TextLabel color="text.g1">-</TextLabel>
                <Avatar size="md">
                  <AvatarImage src={employee.imageUrl} alt="Avatar" />
                </Avatar>
                <TextBodyMedium ml="8px" color="text.g1">
                  {employee.value.displayName}
                </TextBodyMedium>
              </Flex>
            </Flex>
          </GridItem>
        </Grid>
      </CardContent>
    </Card>
  );
};

export { PublicPraiseCard };
